import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  MessagesSquare,
  LineChart,
  BookOpen,
  Wrench,
  ShieldCheck,
  CheckCircle2,
  Coins,
  Plug,
  GraduationCap,
  Rocket,
  ShoppingBag,
  LifeBuoy,
  Boxes,
  Code2,
  PlayCircle,
  Ticket,
  Wand2,
  Languages,
  Minus,
  Plus,
} from 'lucide-react'
import { LogoMark } from '@/components/brand/logo'
import { WhatsAppWidget } from '@/components/whatsapp-widget'
import { useDocumentTitle } from '@/lib/use-document-title'
import { cn } from '@/lib/utils'
import {
  ConversationMock,
  ConversationsTableMock,
  LeadMock,
  InsightsMock,
} from './landing-mocks'

/**
 * Public landing page at `/`. Renders without authentication. Marketing-
 * adjacent material (about, privacy, terms, support, careers, contact)
 * lives on pressone.africa, this page links out to it. The product
 * itself is what's inside `/login`, `/dashboard`, etc.
 *
 * Auth-aware top nav: when the visitor already has an active session, the
 * "Sign in" CTA flips to "Open dashboard" so a returning customer can skip
 * the landing surface and head straight into the workspace.
 *
 * Layout follows the "centered hero + large product mockup, alternating
 * split sections, colored band, analytics showcase, FAQ, gradient CTA"
 * rhythm of the reference template. Product visuals are CSS-rendered mock
 * UIs (see landing-mocks.tsx), no image assets. Brand palette throughout:
 * Geist, brand-blue #2D71F3, brand-navy #0B1832, gold + green accents.
 */
export function Landing() {
  useDocumentTitle('AI Workspace by PressOne Africa')

  const [signedIn, setSignedIn] = useState<boolean | null>(null)

  useEffect(() => {
    // Silent auth probe. httpOnly cookie session (NOT a localStorage token,
    // those are XSS-exfiltratable). Plain fetch (not the api.ts wrapper)
    // because the wrapper auto-redirects to /login on 401, which would yank
    // the visitor away from the landing surface. The landing page is
    // reachable to logged-out users *by design*.
    fetch('/api/auth/me', { credentials: 'same-origin' })
      .then((res) => setSignedIn(res.ok))
      .catch(() => setSignedIn(false))
  }, [])

  return (
    <div className="min-h-screen flex flex-col bg-bg text-text">
      <TopNav signedIn={signedIn} />
      <main id="main-content" tabIndex={-1} className="flex-1 outline-none">
        <Hero signedIn={signedIn} />
        <TrustStrip />
        <Features />
        <FullContext />
        <HumanAI />
        <SpeedClarity />
        <HowItWorks />
        <Insights />
        <UseCases />
        <Developers />
        <Faq />
        <CallToAction signedIn={signedIn} />
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  )
}

/* ---------- Reusable CTA buttons ---------- */

function PrimaryCta({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 rounded-xl bg-brand-blue text-white font-semibold text-base h-12 px-6 shadow-brand hover:bg-brand-blue-2 active:scale-[0.98] transition-all"
    >
      {children}
      <ArrowRight className="h-4 w-4" />
    </Link>
  )
}

function SecondaryCta({
  href,
  to,
  icon: Icon,
  children,
}: {
  href?: string
  to?: string
  icon?: typeof PlayCircle
  children: React.ReactNode
}) {
  const className =
    'inline-flex items-center gap-2 rounded-xl border border-border-strong/60 bg-surface text-text-strong font-semibold text-base h-12 px-6 hover:bg-surface-2 transition-colors'
  const content = (
    <>
      {Icon && <Icon className="h-4 w-4 text-brand-blue" />}
      {children}
    </>
  )
  return to ? (
    <Link to={to} className={className}>
      {content}
    </Link>
  ) : (
    <a href={href} className={className}>
      {content}
    </a>
  )
}

/* ---------- Top nav ---------- */

function TopNav({ signedIn }: { signedIn: boolean | null }) {
  return (
    <header className="sticky top-0 z-30 bg-surface border-b border-border">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="inline-flex" aria-label="PressOne AI">
          <LogoMark size={30} tag="AI" />
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-muted">
          <a href="#features" className="transition-colors hover:text-text-strong">
            Features
          </a>
          <a href="#how-it-works" className="transition-colors hover:text-text-strong">
            How it works
          </a>
          <a href="#use-cases" className="transition-colors hover:text-text-strong">
            Use cases
          </a>
          <a href="#developers" className="transition-colors hover:text-text-strong">
            Developers
          </a>
          <a
            href="https://pressone.africa"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 transition-colors hover:text-text-strong"
          >
            About PressOne
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 text-sm">
          {signedIn ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 rounded-xl bg-brand-blue text-white font-semibold h-10 px-4 shadow-brand hover:bg-brand-blue-2 active:scale-[0.98] transition-all"
            >
              Open dashboard
              <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden sm:inline-flex px-3 py-2 font-medium text-text hover:text-text-strong transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-blue text-white font-semibold h-10 px-4 shadow-brand hover:bg-brand-blue-2 active:scale-[0.98] transition-all"
              >
                Get started
                <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

/* ---------- Hero (centered) ---------- */

function Hero({ signedIn }: { signedIn: boolean | null }) {
  return (
    <section className="relative overflow-hidden">
      {/* Soft brand-tinted radials on the light surface. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(55% 40% at 50% 0%, rgba(45,113,243,0.12) 0%, transparent 60%), radial-gradient(40% 35% at 88% 8%, rgba(255,194,13,0.08) 0%, transparent 60%)',
        }}
      />
      <div className="absolute inset-0 bg-dot-grid opacity-30 pointer-events-none" />

      <div className="relative w-full mx-auto px-5 sm:px-8 min-h-[calc(100vh-72px)] flex flex-col items-center justify-center py-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-blue-soft text-brand-blue border border-brand-blue/15 text-[11px] uppercase tracking-[0.14em] font-semibold">
          <Sparkles className="h-3 w-3" />
          <span>A PressOne Africa product</span>
        </div>
        <h1 className="mt-6 mx-auto max-w-5xl text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold leading-[1.05] tracking-tight text-text-strong">
          A <span className="text-brand-blue">trained digital employee</span> for every customer
          conversation.
        </h1>
        <p className="mt-5 mx-auto max-w-2xl text-muted text-lg leading-relaxed">
          PressOne AI handles WhatsApp, Instagram and voice notes, instantly, professionally,
          and always on brand. Track every conversation, coach your team with AI-driven insights,
          and let the routine work resolve itself.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {signedIn ? (
            <PrimaryCta to="/dashboard">Open dashboard</PrimaryCta>
          ) : (
            <>
              <PrimaryCta to="/signup">Create workspace</PrimaryCta>
              <SecondaryCta href="#how-it-works" icon={PlayCircle}>
                See how it works
              </SecondaryCta>
            </>
          )}
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted">
          <span className="inline-flex items-center gap-1.5">
            <Coins className="h-3.5 w-3.5 text-brand-gold" />
            Pay-as-you-go credits
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-brand-green" />
            Top up as you go
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Languages className="h-3.5 w-3.5 text-brand-blue" />
            Speaks your customers&apos; language
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-brand-blue" />
            Built &amp; supported in Africa
          </span>
        </div>
      </div>
    </section>
  )
}

/* ---------- Trust strip ---------- */

function TrustStrip() {
  return (
    <section className="border-y border-border bg-surface-2/40">
      <div className="max-w-6xl mx-auto flex flex-col items-center gap-3 px-5 sm:px-8 py-7 text-center md:flex-row md:justify-between md:text-left">
        <p className="text-[13px] text-muted">
          Built by{' '}
          <a
            href="https://pressone.africa"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-text-strong hover:underline"
          >
            PressOne Africa
          </a>
          , already powering business communication for Sterling Bank, Shuttlers, SeamlessHR and
          others.
        </p>
        <a
          href="https://pressone.africa"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[13px] font-medium text-brand-blue hover:underline"
        >
          See the call platform
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  )
}

/* ---------- Section heading helper ---------- */

function SectionHeading({
  eyebrow,
  title,
  copy,
}: {
  eyebrow: string
  title: React.ReactNode
  copy?: string
}) {
  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-blue mb-3">
        {eyebrow}
      </div>
      <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-text-strong leading-tight">
        {title}
      </h2>
      {copy && <p className="text-muted text-lg mt-3 leading-relaxed">{copy}</p>}
    </div>
  )
}

/* ---------- Features (accent cards) ---------- */

type FeatureAccent = 'blue' | 'green' | 'gold' | 'navy'

const FEATURE_ACCENTS: Record<FeatureAccent, { tile: string; glow: string }> = {
  blue: {
    tile: 'bg-brand-blue-soft text-brand-blue ring-1 ring-brand-blue/15',
    glow: 'bg-brand-blue/25',
  },
  green: {
    tile: 'bg-brand-green-soft text-brand-green ring-1 ring-brand-green/20',
    glow: 'bg-brand-green/25',
  },
  gold: {
    tile: 'bg-brand-gold-soft text-brand-navy ring-1 ring-brand-gold/30',
    glow: 'bg-brand-gold/25',
  },
  navy: {
    tile: 'bg-brand-navy-soft text-brand-navy ring-1 ring-brand-navy/15',
    glow: 'bg-brand-navy/20',
  },
}

const FEATURES: {
  icon: typeof Sparkles
  title: string
  copy: string
  accent: FeatureAccent
}[] = [
  {
    icon: MessagesSquare,
    title: 'Multi-channel coverage',
    copy: 'WhatsApp, Instagram, web chat and voice notes, even images and PDFs, in one shared inbox with one consistent voice.',
    accent: 'blue',
  },
  {
    icon: BookOpen,
    title: 'Trained on your business',
    copy: 'Add your FAQs, policies and product info. Your digital employee answers grounded in your knowledge, with citations, never made up.',
    accent: 'green',
  },
  {
    icon: Wrench,
    title: 'Real actions, not just replies',
    copy: 'Connect your APIs and forms. The AI can register customers, look up orders and trigger refunds, whatever your team would do.',
    accent: 'gold',
  },
  {
    icon: Ticket,
    title: 'Built-in ticketing',
    copy: 'Auto or manual tickets with AI-suggested assignees, internal notes, tags, priority and SLA tracking, bulk actions and CSV export.',
    accent: 'navy',
  },
  {
    icon: Wand2,
    title: 'AI copilot built in',
    copy: 'Drafts your agent config, intents and flows, suggests replies after a handoff, and answers your team from your own knowledge.',
    accent: 'blue',
  },
  {
    icon: LineChart,
    title: 'Coaching insights',
    copy: 'See where conversations escalate, which intents are spiking, and where AI confidence dips. Coach with evidence, not vibes.',
    accent: 'green',
  },
]

function Features() {
  return (
    <section id="features" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <SectionHeading
        eyebrow="Built for teams, not just replies"
        title="A complete customer-conversation layer for your business."
        copy="Not a chatbot. A trained digital employee that handles the routine and escalates the rest, with full visibility for your team."
      />
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f, i) => {
          const a = FEATURE_ACCENTS[f.accent]
          return (
            <article
              key={f.title}
              className="group relative flex flex-col overflow-hidden rounded-[28px] bg-surface p-6 shadow-2 transition-all duration-200 hover:-translate-y-1 hover:shadow-3"
            >
              {/* Soft corner glow that warms on hover */}
              <span
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full blur-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100',
                  a.glow,
                )}
              />
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'inline-flex h-12 w-12 items-center justify-center rounded-xl transition-transform group-hover:scale-105',
                    a.tile,
                  )}
                >
                  <f.icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="text-[12px] font-semibold tabular-nums tracking-[0.18em] text-muted/40">
                  0{i + 1}
                </span>
              </div>
              <h3 className="mt-5 text-base font-semibold tracking-tight text-text-strong">
                {f.title}
              </h3>
              <p className="mt-2 text-[13px] text-muted leading-relaxed">{f.copy}</p>
            </article>
          )
        })}
      </div>
    </section>
  )
}

/* ---------- Split section helper ---------- */

function SplitSection({
  eyebrow,
  title,
  copy,
  bullets,
  visual,
  reverse,
}: {
  eyebrow: string
  title: string
  copy: string
  bullets: string[]
  visual: React.ReactNode
  reverse?: boolean
}) {
  return (
    <div className="grid items-center gap-10 md:grid-cols-2 lg:gap-14">
      <div className={cn(reverse && 'md:order-2')}>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-blue mb-3">
          {eyebrow}
        </div>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-text-strong leading-tight">
          {title}
        </h2>
        <p className="mt-3 text-muted leading-relaxed">{copy}</p>
        <ul className="mt-6 space-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-[14px] text-text">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
              {b}
            </li>
          ))}
        </ul>
      </div>
      <div className={cn('flex justify-center', reverse && 'md:order-1')}>{visual}</div>
    </div>
  )
}

/* ---------- Full context ---------- */

function FullContext() {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
      <SplitSection
        eyebrow="Full context, every time"
        title="Every contact, with the whole story attached."
        copy="Each lead carries its email, phone, channel and source, plus every conversation and ticket it's connected to. Your team always has the full picture."
        bullets={[
          'Lead profile with email, phone, channel and source',
          'Every conversation linked back to the lead',
          'Tickets tied to the originating conversation',
          'Search any contact by name, phone or email',
        ]}
        visual={<LeadMock />}
        reverse
      />
    </section>
  )
}

/* ---------- Human + AI (colored band) ---------- */

function HumanAI() {
  return (
    <section className="relative overflow-hidden border-y border-border">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(135deg, rgba(45,113,243,0.10) 0%, rgba(63,112,235,0.06) 45%, rgba(27,222,108,0.05) 100%)',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <SplitSection
          eyebrow="Human + AI, working together"
          title="The AI handles the routine. Your team handles what matters."
          copy="Replies stay grounded in your knowledge base, every answer cites its sources. When a conversation needs judgement, it escalates to a human with the full transcript intact."
          bullets={[
            'AI answers grounded in your knowledge, with citations',
            'Per-message confidence, tokens and response time',
            'Automatic handoff to a human when needed',
            'Every AI action logged and reviewable',
          ]}
          visual={<ConversationMock className="w-full max-w-md" />}
        />
      </div>
    </section>
  )
}

/* ---------- Speed & clarity ---------- */

function SpeedClarity() {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
      <SplitSection
        eyebrow="One workspace to review it all"
        title="Every conversation, searchable and accountable."
        copy="Filter by channel and status, see message volumes at a glance, and drill into any transcript, so nothing slips and every exchange stays accountable."
        bullets={[
          'Filter by channel: WhatsApp, Instagram and web chat',
          'Status at a glance: active, handoff, ended',
          'Search by conversation ID or customer',
          'Drill into full transcripts in one click',
        ]}
        visual={<ConversationsTableMock className="w-full max-w-lg" />}
        reverse
      />
    </section>
  )
}

/* ---------- How it works ---------- */

const STEPS: { n: string; icon: typeof Plug; title: string; body: string }[] = [
  {
    n: '01',
    icon: Plug,
    title: 'Connect your channels',
    body: 'Link WhatsApp, Instagram and your web chat in minutes. Everything lands in one shared inbox your team already knows.',
  },
  {
    n: '02',
    icon: GraduationCap,
    title: 'Train on your business',
    body: 'Add your FAQs, policies and product info. Your digital employee answers grounded in your knowledge, and you tune its voice.',
  },
  {
    n: '03',
    icon: Rocket,
    title: 'Go live and coach',
    body: 'Top up credits and switch it on. The AI resolves the routine, escalates the rest, and shows you where to coach the team.',
  },
]

function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-surface-2/40 border-y border-border py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow="How it works" title="Three steps. Live in under an hour." />
        <ol className="mt-12 grid gap-4 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={s.n} className="relative rounded-[28px] border border-border bg-surface p-6">
              <div className="flex items-center justify-between">
                <span className="inline-flex h-9 items-center rounded-full bg-brand-navy px-3 text-[12px] font-semibold tracking-[0.14em] text-white">
                  STEP {s.n}
                </span>
                <s.icon className="h-5 w-5 text-brand-blue" strokeWidth={2} />
              </div>
              <h3 className="mt-4 text-lg font-semibold tracking-tight text-text-strong">
                {s.title}
              </h3>
              <p className="mt-2 text-[14px] text-muted leading-relaxed">{s.body}</p>
              {i < STEPS.length - 1 && (
                <ArrowRight className="absolute -right-2 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-border-strong md:block" />
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/* ---------- Insights / analytics ---------- */

function Insights() {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
      <SplitSection
        eyebrow="Know what's happening, instantly"
        title="Turn conversation data into clear operational insight."
        copy="Total conversations, handoff rate and AI confidence, plus ticket trends and the reasons behind every escalation, so decisions are based on reality, not assumptions."
        bullets={[
          'Track total conversations and average messages',
          'Monitor handoff rate and AI confidence',
          'See ticket volume: created vs resolved',
          'Understand exactly why conversations escalate',
        ]}
        visual={<InsightsMock className="max-w-md" />}
      />
    </section>
  )
}

/* ---------- Use cases ---------- */

const USE_CASES: { icon: typeof ShoppingBag; title: string; body: string }[] = [
  {
    icon: ShoppingBag,
    title: 'Sales & lead capture',
    body: 'Answer product questions, send payment links and capture every lead, even at 2am, even when your team is asleep.',
  },
  {
    icon: LifeBuoy,
    title: 'Support deflection',
    body: 'Resolve the repetitive "where is my order?" and "how do I…" questions automatically, and hand the hard ones to a human with full context.',
  },
  {
    icon: Boxes,
    title: 'Embed in your product',
    body: 'Building your own app? Wire the AI agent into your product over our REST API and let it handle conversations end to end.',
  },
]

function UseCases() {
  return (
    <section id="use-cases" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <SectionHeading eyebrow="Who it's for" title="One digital employee, several jobs." />
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {USE_CASES.map((c) => (
          <article
            key={c.title}
            className="rounded-[28px] border border-border bg-surface p-6 transition-shadow hover:shadow-2"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-blue-soft text-brand-blue">
              <c.icon className="h-5 w-5" strokeWidth={2} />
            </span>
            <h3 className="mt-4 text-lg font-semibold tracking-tight text-text-strong">{c.title}</h3>
            <p className="mt-2 text-[14px] text-muted leading-relaxed">{c.body}</p>
          </article>
        ))}
      </div>
    </section>
  )
}

/* ---------- Developer callout ---------- */

function Developers() {
  return (
    <section id="developers" className="border-y border-border bg-brand-navy py-20 text-white md:py-24">
      <div className="max-w-6xl mx-auto grid gap-10 px-5 sm:px-8 md:grid-cols-[1fr_1.1fr] md:items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
            <Code2 className="h-3 w-3" strokeWidth={2.5} />
            For developers
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-semibold leading-tight tracking-tight">
            Wire the AI into your own product.
          </h2>
          <p className="mt-3 max-w-md text-[15px] leading-relaxed text-white/70">
            Manage tickets, flows and conversations with a clean REST API. Issue an API key,
            hit predictable endpoints, ship.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/signup"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-white px-5 text-sm font-semibold tracking-tight text-brand-navy transition-colors hover:bg-white/90"
            >
              Get an API key
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://pressone.africa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/25 px-5 text-sm font-semibold tracking-tight text-white transition-colors hover:bg-white/10"
            >
              Talk to us
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
        <pre className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.04] p-5 text-[12px] leading-relaxed text-white/85 font-mono backdrop-blur-sm">
          <code>
{`curl https://aichat.pressone.africa/api/v1/organizations/$ORG_ID/tickets \\
  -H "X-API-Key: $API_KEY"

# → 200 OK
# {
#   "tickets": [
#     { "id": "tkt_a91b…", "status": "open",
#       "subject": "Refund for order #4821" }
#   ],
#   "page": 1, "per_page": 20
# }`}
          </code>
        </pre>
      </div>
    </section>
  )
}

/* ---------- FAQ ---------- */

const FAQS: { q: string; a: string }[] = [
  {
    q: 'How fast can we get started?',
    a: 'Most teams are live in under an hour. Connect your channels, upload your knowledge, set supervision rules, and switch it on, no engineers, no long setup.',
  },
  {
    q: 'Do we need technical skills?',
    a: 'No. You train the AI by uploading FAQs, policies and product docs, no code required. Developers can optionally go deeper with our REST API.',
  },
  {
    q: 'Can we keep our existing WhatsApp number?',
    a: 'Yes. PressOne AI works alongside your existing business number and channels, your customers keep messaging you the way they already do.',
  },
  {
    q: 'How does billing work?',
    a: 'Pay-as-you-go message credits, topped up whenever you need them. No subscription tiers, no trial games, you only pay for the conversations your digital employee handles.',
  },
  {
    q: 'Can this handle high message volumes?',
    a: 'Yes, it is designed for scale. The AI resolves routine conversations in parallel and escalates the exceptions to your team with full context.',
  },
]

function Faq() {
  const [open, setOpen] = useState<number | null>(0)
  return (
    <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
      <SectionHeading eyebrow="FAQ" title="Frequently asked questions" />
      <div className="mt-10 divide-y divide-border rounded-[28px] border border-border bg-surface">
        {FAQS.map((f, i) => {
          const isOpen = open === i
          return (
            <div key={f.q}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span className="text-[15px] font-semibold text-text-strong">{f.q}</span>
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-2 text-brand-blue">
                  {isOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                </span>
              </button>
              {isOpen && (
                <p className="px-5 pb-5 -mt-1 text-[14px] text-muted leading-relaxed">{f.a}</p>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

/* ---------- Final CTA (gradient band) ---------- */

function CallToAction({ signedIn }: { signedIn: boolean | null }) {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
      <div className="relative overflow-hidden rounded-3xl px-6 py-16 text-center sm:px-10 md:py-20">
        {/* Brand gradient wash, the reference's signature closing band */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(160deg, #e9f0fd 0%, #cfe0fb 38%, #2D71F3 100%)',
          }}
        />
        <div
          className="absolute -right-20 -bottom-24 h-72 w-72 opacity-[0.12] brand-flourish pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative">
          <h2 className="mx-auto max-w-xl text-3xl sm:text-4xl font-semibold leading-tight tracking-tight text-brand-navy">
            See how WhatsApp should work for your business.
          </h2>
          <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-brand-navy/75">
            Sign up, top up credits, start sending. Replace manual replies with an AI system built
            for structured workflows, operational control and high-volume messaging.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {signedIn ? (
              <PrimaryCta to="/dashboard">Open dashboard</PrimaryCta>
            ) : (
              <>
                <PrimaryCta to="/signup">Create workspace</PrimaryCta>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-xl border border-brand-navy/20 bg-white/70 text-brand-navy font-semibold text-base h-12 px-6 hover:bg-white transition-colors"
                >
                  I already have an account
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ---------- Footer ---------- */

function Footer() {
  // Everything marketing-y points back to pressone.africa. The product app
  // is the workspace; the marketing site is the source of truth for company
  // / legal / pricing / careers content.
  const linkGroups: {
    heading: string
    note?: string
    links: { label: string; href: string }[]
  }[] = [
    {
      heading: 'Product',
      links: [
        { label: 'Features', href: '#features' },
        { label: 'How it works', href: '#how-it-works' },
        { label: 'Use cases', href: '#use-cases' },
        { label: 'For developers', href: '#developers' },
      ],
    },
    {
      heading: 'Use cases',
      links: [
        { label: 'Customer service teams', href: '#use-cases' },
        { label: 'Sales & lead capture', href: '#use-cases' },
        { label: 'Support deflection', href: '#use-cases' },
      ],
    },
    {
      heading: 'Company',
      note: 'On pressone.africa',
      links: [
        { label: 'About PressOne', href: 'https://pressone.africa/about' },
        { label: 'Contact', href: 'https://pressone.africa/contact' },
        { label: 'Careers', href: 'https://pressone.africa/careers' },
      ],
    },
    {
      heading: 'Legal',
      note: 'On pressone.africa',
      links: [
        { label: 'Privacy', href: 'https://pressone.africa/privacy' },
        { label: 'Terms', href: 'https://pressone.africa/terms' },
      ],
    },
  ]

  return (
    <footer className="relative border-t border-border bg-bg overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_repeat(4,1fr)]">
        <div>
          <LogoMark size={30} tag="AI" />
          <p className="mt-4 max-w-xs text-[13px] leading-relaxed text-muted">
            A PressOne Africa product. Helping African businesses build trust and run their customer
            conversations with confidence.
          </p>
        </div>
        {linkGroups.map((group) => (
          <div key={group.heading}>
            <div className="text-[12px] uppercase tracking-[0.16em] font-semibold text-text-strong">
              {group.heading}
            </div>
            {group.note && <div className="mt-1 text-[11px] text-muted/80">{group.note}</div>}
            <ul className="mt-4 flex flex-col gap-2.5 text-sm">
              {group.links.map((link) => {
                const external = link.href.startsWith('http')
                const anchor = link.href.startsWith('#')
                return (
                  <li key={link.label}>
                    {external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[13px] text-muted hover:text-text-strong transition-colors"
                      >
                        {link.label}
                        <ArrowUpRight className="h-3 w-3 opacity-60" />
                      </a>
                    ) : anchor ? (
                      <a
                        href={link.href}
                        className="text-[13px] text-muted hover:text-text-strong transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-[13px] text-muted hover:text-text-strong transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="relative border-t border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-muted">
          <div>© {new Date().getFullYear()} PressOne Africa. All rights reserved.</div>
          <div>
            Made with <span className="text-brand-red">♥</span> in Lagos.
          </div>
        </div>
      </div>
    </footer>
  )
}
